import { defineComponent as _defineComponent } from 'vue'
import lodashUniqueId from 'lodash.uniqueid'
import Trix from 'trix'
import { nextTick, ref, watch } from 'vue'

interface TrixType {
  [k: string]: any
}


export default /*#__PURE__*/_defineComponent({
  __name: 'rich_text_editor',
  props: {
    placeholder: null,
    value: null,
    dir: null,
    toolbarId: null,
    ariaLabel: null
  },
  emits: [
  'initialize',
  'focus',
  'selectionchange',
  'attributeschange',
  'blur',
  'input',
  'fileaccept',
  'enter',
  'arrow-down-on-last-line',
  'arrow-up-on-first-line',
  'paste',
],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  placeholder: string
  value: string
  dir: string
  toolbarId?: string
  ariaLabel?: string
}



const urlRegex = /^(https?:\/\/(?:www\.|(?!www))[^\s.]+\.[^\s]{2,}|www\.[^\s]+\.[^\s]{2,})$/gi

Trix.config.textAttributes.sup = {
  tagName: 'sup',
  inheritable: true,
}

Trix.config.textAttributes.sub = {
  tagName: 'sub',
  inheritable: true,
}

Trix.config.textAttributes.mark = {
  tagName: 'mark',
  inheritable: true,
}

Trix.config.blockAttributes.math = {
  tagName: 'var',
  terminal: true,
  text: {
    plaintext: true,
  },
}

const editor = ref<TrixType>({})
const uniqueId = lodashUniqueId('rte')
const editorEl = ref<HTMLInputElement | null>(null)
const frozenSelectionRange = ref<number[] | null>(null)

expose({
  editor,
  freezeSelection,
  defreezeSelection,
  focus,
  activateAttribute,
  deactivateAttribute,
  moveCursorToEndOfSelection,
  html,
  activeAttributes,
  selectedText,
  selectionCoordinates,
  onInitialize,
  onFocus,
  onBlur,
  onFocusIn,
  onSelectionChange,
  onAttributesChange,
  onInput,
  onFileAccept,
  onEnter,
})



watch(
  () => props.value,
  (newValue) => {
    if (editor.value && html() !== newValue) {
      editor.value.loadHTML(newValue)
    }
  },
)

function freezeSelection() {
  editor.value.activateAttribute('frozen')
  frozenSelectionRange.value = editor.value.getSelectedRange()
}

function defreezeSelection() {
  if (frozenSelectionRange.value) {
    editor.value.setSelectedRange(frozenSelectionRange.value)
    frozenSelectionRange.value = null
  }
  editor.value && editor.value.deactivateAttribute('frozen')
}

function focus() {
  // nextTick - ensures that the input element has completed rendering in the DOM
  // setTimeout -  defers the focusing event until after the current event propagation.
  //
  // If you click on a button that triggers this method and don't have setTimeout,
  // what can happen is: focus is shifted to this input, then immediately shifted to the button.
  nextTick(() => {
    setTimeout(() => {
      // if the post is deleted remotely, editorEl.value will become undefined
      if (editorEl.value) {
        editorEl.value?.focus()
      }
    })
  })
}

function activateAttribute(name, val) {
  defreezeSelection()
  editor.value.activateAttribute(name, val)
  if (name === 'href') {
    moveCursorToEndOfSelection()
  }
}

function deactivateAttribute(name) {
  editor.value.deactivateAttribute(name)
}

function moveCursorToEndOfSelection() {
  const currentSelectedRange = editor.value.getSelectedRange()
  if (currentSelectedRange && currentSelectedRange[0] !== currentSelectedRange[1]) {
    editor.value.setSelectedRange(currentSelectedRange[1])
  }
}

function html() {
  return editorEl.value?.value || ''
}

function activeAttributes() {
  return editor.value.composition.currentAttributes
}

function selectedText() {
  if (!editor.value) return ''
  if (!editor.value.getDocument) return ''
  const currentSelection = editor.value.getSelectedRange()
  if (!currentSelection) return ''
  return editor.value.getDocument().getStringAtRange(currentSelection)
}
function selectionCoordinates() {
  if (!editor.value) return {}
  if (!editor.value.getDocument) return {}
  const currentSelection = editor.value.getSelectedRange()
  if (!currentSelection) return {}
  const clientRect = editor.value.getClientRectAtPosition(currentSelection[0])
  if (clientRect) {
    return { x: clientRect.x, y: clientRect.y }
  } else {
    return {}
  }
}

function onInitialize(event) {
  editor.value = event.target.editor
  editor.value.loadHTML(props.value)
  emit('initialize')
}

function onFocus() {
  emit('focus')
}

function onBlur() {
  emit('blur')
}

function onFocusIn() {
  defreezeSelection()
}

function onSelectionChange() {
  emit('selectionchange', selectedText(), selectionCoordinates())
}

function onAttributesChange() {
  emit('attributeschange', activeAttributes())
}

function onInput() {
  emit('input', html())
}

function onFileAccept(e) {
  emit('fileaccept', e)
}

function onEnter() {
  emit('enter')
}

function onAttachmentAdd(e) {
  if (e.attachment) {
    e.attachment.remove()
  }

  // If jQuery
  if (e.originalEvent && e.originalEvent.attachment) {
    e.originalEvent.attachment.remove()
  }
}

function autolinkPastedUrl(e: ClipboardEvent) {
  setTimeout(() => {
    const clip = e.clipboardData
    let pastedText
    if (clip && typeof clip.getData === 'function') {
      pastedText = clip.getData('Text')
    }
    if (pastedText) {
      if ((pastedText as string).match(urlRegex)) {
        const element = e.currentTarget as Trix
        const editor = element.editor
        const currentText = editor.getDocument().toString()
        const currentSelection = element.editor.getSelectedRange()
        // text up to the cursor position
        const textWeAreInterestedIn = currentText.substring(0, currentSelection[0])
        // search for the start of the url
        const startOfPastedText = textWeAreInterestedIn.lastIndexOf(pastedText)
        // add an undo entry so people can undo the autolinking
        editor.recordUndoEntry('Auto Link Paste')
        // select the url text
        editor.setSelectedRange([startOfPastedText, currentSelection[0]])
        // add a hyperlink to it
        editor.activateAttribute('href', pastedText)
        // go back to the original selection
        return editor.setSelectedRange(currentSelection)
      }
    }
    return true
  }, 0)
}

function sanitizePastedHtml(e) {
  if (!e?.paste?.html) {
    return
  }

  const div = document.createElement('div')
  div.innerHTML = e.paste.html

  // remove all heading tags for a11y
  Array.from(div.getElementsByTagName('*')).forEach((element) => {
    if (/^h\d$/i.test(element.nodeName)) {
      element.outerHTML = `<div>${element.innerHTML}</div>`
    }
  })

  e.paste.html = div.innerHTML
}

function onArrowDown() {
  // emit an event when arrow down key is pressed and the cursor is on the last line of the editor
  // the default behaviour for arrow down on the last line is move to the end of the line
  // we only emit a special event to be handled if the cursor is already at the end of the last line => check editor.value.getSelectedRange()[0] === value.length
  if (editor.value.getSelectedRange()[0] === editor.value.getDocument().toString().length - 1) {
    emit('arrow-down-on-last-line')
  }
}

function onArrowUp() {
  // emit an event when arrow up key is pressed and the cursor is on the first line of the editor
  // the default behaviour for arrow up on the first line is move to the start of the line
  // we only emit a special event to be handled if the cursor is already at the start of the first line => check editor.value.getSelectedRange()[0] === 0
  if (editor.value.getSelectedRange()[0] === 0) {
    emit('arrow-up-on-first-line')
  }
}

return { __sfc: true,props, urlRegex, editor, uniqueId, editorEl, frozenSelectionRange, emit, freezeSelection, defreezeSelection, focus, activateAttribute, deactivateAttribute, moveCursorToEndOfSelection, html, activeAttributes, selectedText, selectionCoordinates, onInitialize, onFocus, onBlur, onFocusIn, onSelectionChange, onAttributesChange, onInput, onFileAccept, onEnter, onAttachmentAdd, autolinkPastedUrl, sanitizePastedHtml, onArrowDown, onArrowUp }
}

})